import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {RootState} from "../store";
import {GridRefUtils} from "../../../../utils/helpers/GridRefPreference";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";
import mapboxgl from "mapbox-gl";
import {SavedCenter} from "../../../../utils/models/MapStyles";
import {getElevation} from "../../../../utils/crosshair_altitude_service/pixel_methods";
import {IndexedDBCache} from "../../../../utils/crosshair_altitude_service/indexed_db_cache";
import {throttle} from 'lodash-es';
import graph_client from "../../../../utils/graphql/graph_client";
import {CreatePrintMap, CreatePrintMaps} from "../../../../utils/graphql/print-maps-graph";
import {getFeatureCollectionBounds} from "../../../../utils/helpers/GetBoundsFromGeoJsonPointsFeatureCollection";
import {MapLayerCategory} from "../../../../utils/layer-registry";
import type { FeatureCollection } from 'geojson';
import { buildPrintPreviewPaintExpressions } from "../../../../utils/printMapRouteStyleExpressions";


export class PrintMapState {
  center: mapboxgl.LngLatLike = [0,0]
  miniCoordsBar: string = ''
  mapInitialised: boolean = false
  zoom: number = 0
  loading: boolean = false
  pitch: number = 0
  bearing: number = 0
  marker: mapboxgl.Marker | null = null
  featureCollection: FeatureCollection | null = null
  featureCollectionSourceId: string | null = null
}

const FEATURE_COLLECTION_SOURCE_ID = 'print-map-feature-collection';

const actions: ActionTree<PrintMapState, RootState> = {
  mapInitialised(store) {
    store.commit('setMapInitialised', true)
    const gridRef = GridRefUtils.preference()
    let measurementPreference = MeasurementPrefUtility.preference()
    window.map.on('click', (e) => {
      store.dispatch('mapClicked', e);
    });

    const cache = new IndexedDBCache();
    const handleMouseMove = async (e) => {
      const coords = gridRef.convert(e.lngLat);
      const elevation = await getElevation(e.lngLat, e.target.getZoom(), cache);
      store.commit('setMiniCoordsBar', `${coords.toString()} ${elevation}`);
    };
    const throttledMouseMove = throttle(handleMouseMove, 20); // Adjust the throttle interval as needed
    window.map.on('mousemove', throttledMouseMove);

  },
  mapClicked(store, e) {
    console.log('map clicked', e)
  },
  printMap(store) {
    window.map.once('idle', () => {
      store.commit('setLoading', false)
      window.print()
    })
  },
  addFeatureCollection(store, payload) {
    const normalisedPayload = payload?.type === 'FeatureCollection'
      ? { featureCollection: payload as FeatureCollection, fitBounds: true }
      : payload;

    const featureCollection: FeatureCollection | undefined = normalisedPayload?.featureCollection;
    const fitBounds = normalisedPayload?.fitBounds ?? true;

    // Validate the input FeatureCollection
    if (
      !featureCollection ||
      featureCollection.type !== 'FeatureCollection' ||
      !Array.isArray(featureCollection.features)
    ) {
      console.error('Invalid FeatureCollection provided.');
      return;
    }
    store.state.featureCollection = featureCollection

    if (featureCollection.features.length === 0) {
      return;
    }

    const bounds = getFeatureCollectionBounds(featureCollection);

    if (fitBounds) {
      window.map.fitBounds(bounds, {
          padding: 20
      })
    }

    const sourceId = store.state.featureCollectionSourceId || FEATURE_COLLECTION_SOURCE_ID;
    const layerIds = [
      `${sourceId}-line`,
      `${sourceId}-border`,
      `${sourceId}-polygon`,
    ];

    layerIds.forEach((layerId) => {
      if (window.map.getLayer(layerId)) {
        window.map.removeLayer(layerId);
      }
    });

    if (window.map.getSource(sourceId)) {
      window.map.removeSource(sourceId);
    }

    store.state.featureCollectionSourceId = sourceId;

    window.layerRegistry.addSource(sourceId, {
      type: 'geojson',
      data: featureCollection
    });

    // Point layer removed for print maps - we only want to show the route line, not control point markers

    // Pull the user's cloud-synced route style settings (hydrated into
    // localStorage on boot by routeStyleSettingsCloudSync) and apply them
    // here so the preview's route line matches the final PDF rendered by
    // PrintMapRouteStyleResolver on the backend. Without this the preview
    // stays hardcoded crimson at 5px while the PDF respects the user's
    // colour / thickness choices.
    const paintExpressions = buildPrintPreviewPaintExpressions();

    // Add a layer for line and multiline features
    window.layerRegistry.addLayer({
      id: `${sourceId}-line`,
      type: 'line',
      source: sourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': paintExpressions.lineColor,
        'line-width': paintExpressions.lineWidth,
        'line-opacity': paintExpressions.lineOpacity
      },
      filter: ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false]
    }, MapLayerCategory.Annotations, { z: 11});

    window.layerRegistry.addLayer({
      id: `${sourceId}-border`,
      type: 'line',
      source: sourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': paintExpressions.borderColor,
        'line-width': paintExpressions.borderWidth
      },
      filter: ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false]
    }, MapLayerCategory.Annotations, { z: 10});

    window.layerRegistry.addLayer({
      id: `${sourceId}-polygon`,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': '#4BC263',
        'fill-opacity': 0.3
      },
      filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false]
    }, MapLayerCategory.Annotations, { z: 10});
  },
  async createPrintMap(store, { bounds, title, center, zoom, orientation, pageSize }) {
    const defaultTitle = (
      (window as typeof window & { defaultPrintMapTitle?: string }).defaultPrintMapTitle ?? "HiiKER Print Map"
    );
    const variables = {
      printMap: {
        mapType: store.rootState.mapStyles.currentMapStyle?.toPrintMapString() ?? "standard-print-map",
        geometry: store.state.featureCollection,
        title: title && title.trim() ? title : defaultTitle,
        bounds: bounds,
        zoom: zoom,
        center: center,
        orientation: orientation,
        pageSize: pageSize || 'a4',
      }
    };

    const response = await graph_client.request<{ createPrintMap: unknown }>(CreatePrintMap, variables);
    store.commit('setLoading', false);
    return response.createPrintMap;
  },
  /**
   * Queue a whole pile of sheets in one request.
   *
   * Returns immediately with the created jobs so the page can drop them into
   * the print-queue drawer. The old flow blocked on a ten-second timer and
   * then hard-navigated to /user/print-maps, which threw away whatever the
   * user had framed and told them nothing about progress.
   */
  async createPrintMaps(store, { sheets }) {
    if (!Array.isArray(sheets) || sheets.length === 0) {
      return { printMaps: [], errors: [] };
    }

    store.commit('setLoading', true);
    try {
      const response = await graph_client.request<{
        createPrintMaps: { printMaps: unknown[]; errors: Array<{ index: number; message: string }> };
      }>(CreatePrintMaps, { printMaps: sheets });
      return response.createPrintMaps ?? { printMaps: [], errors: [] };
    } finally {
      store.commit('setLoading', false);
    }
  }
}

const mutations: MutationTree<PrintMapState> = {
  setCenter(state, center: mapboxgl.LngLatLike) {
    state.center = center
  },
  setMiniCoordsBar(state, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar
  },
  setMapInitialised(state, mapInitialised: boolean) {
    state.mapInitialised = mapInitialised
  },
  setZoom(state, zoom: number) {
    state.zoom = zoom
  },
  setLoading(state, loading: boolean) {
    state.loading = loading
  },
  setPitch(state, pitch: number) {
    state.pitch = pitch
  },
  setBearing(state, bearing: number) {
    state.bearing = bearing
  }
}

// @ts-ignore
const getters: GetterTree<MapState, RootState> = {
  initialMapSettings(state): mapboxgl.MapboxOptions {
    const savedCenter = SavedCenter.getCurrentCenter()
    return {
      container: 'map', // container ID
      // @ts-ignore
      center: savedCenter.lngLat(), // starting position [lng, lat]
      zoom: savedCenter.zoom, // starting zoom
      pitch: state.pitch,
      bearing: state.bearing,
      maxPitch: 85,
    }
  },
}

const state: PrintMapState = new PrintMapState()

const printMapModule: Module<PrintMapState, RootState> = {
  namespaced: true,
  state: state,
  actions: actions,
  mutations: mutations,
  getters: getters,
}

export default printMapModule
